// Меню учётной записи (верхняя строка редактора и стартового экрана), смена пароля, плашка «чужой отчёт»,
// приглашение заполнить профиль для титула, место и число отчётов.
import { useEffect, useRef, useState } from 'react';
import type { DialogProps } from '../../core/registry';
import { ctx } from '../../core/ctx';
import { saveNow } from '../../core/project';
import { readPref, writePref } from '../../core/store';
import { Btn, MenuList, Modal, Popover } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { getProfileCached, loadProfile, onProfile, type Profile } from '../sto_intel/profile';
import { authApi, displayName, fmtBytes, hueOf, initials, logout, ROLE_NAMES, signedIn, useAuth, type MeUser, type SessionRow } from './session';
import { ErrorBox, errorOf, PasswordField, passwordChecks, Rules } from './fields';

export function Avatar({ user, size = 28 }: { user: Pick<MeUser, 'login' | 'name'> | null | undefined; size?: number }) {
  const h = hueOf(user?.login || '');
  return (
    <span className="au-avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.4), ['--h' as any]: h }} aria-hidden>
      {initials(user)}
    </span>
  );
}

/** Кнопка учётной записи: аватар (+ имя в полном виде) и меню. */
export function AccountMenu({ compact = false }: { compact?: boolean }) {
  const me = useAuth((s) => s.me);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  if (!me) return null;
  const u = me.user;
  const q = me.quota;
  const pct = q.quotaBytes ? Math.min(100, Math.round((q.usedBytes / q.quotaBytes) * 100)) : 0;
  const leave = async () => { await logout(() => saveNow()); };
  return (
    <>
      <button ref={ref} type="button" className={'au-acc' + (compact ? ' is-compact' : '')} onClick={() => setOpen(!open)} data-testid="account-menu"
        aria-haspopup="menu" aria-expanded={open} data-tip={compact ? `${displayName(u)} (${ROLE_NAMES[u.role]})` : undefined}>
        <Avatar user={u} size={compact ? 24 : 28} />
        {!compact && <span className="au-acc-text"><span className="au-acc-name">{displayName(u)}</span>
          <span className="au-acc-role">{u.role === 'admin' ? 'администратор' : u.login}</span></span>}
        {!compact && <Icon name="down" size={12} />}
      </button>
      {open && (
        <Popover anchor={ref.current} onClose={() => setOpen(false)} align="right" className="au-acc-pop">
          <div className="au-acc-head" data-testid="account-head">
            <Avatar user={u} size={40} />
            <div className="au-acc-who">
              <div className="au-acc-bigname">{displayName(u)}</div>
              <div className="au-acc-login">{u.login} · <span className={u.role === 'admin' ? 'au-role-admin' : ''}>{ROLE_NAMES[u.role]}</span></div>
            </div>
          </div>
          <div className="au-acc-quota" data-testid="account-quota">
            <div className="au-acc-qrow"><span>Место</span><b>{fmtBytes(q.usedBytes)}{q.quotaBytes ? ` из ${fmtBytes(q.quotaBytes)}` : ''}</b></div>
            {q.quotaBytes ? <div className={'au-bar' + (pct > 90 ? ' is-full' : '')}><span style={{ width: pct + '%' }} /></div> : null}
            <div className="au-acc-qrow"><span>Отчётов</span><b>{q.projects}{q.maxProjects ? ` из ${q.maxProjects}` : ''}</b></div>
          </div>
          <MenuList onDone={() => setOpen(false)} autoFocus items={[
            { id: 'account-profile', label: 'Мой профиль (данные для титула)', icon: 'student', onClick: () => void ctx.openDialog('sto.profile', {}) },
            ...(me.subscription.exempt ? [] : [{ id: 'account-subscription', label: 'Подписка', icon: 'star', onClick: () => void ctx.openDialog('auth.subscription', {}) }]),
            ...(me.subscription.exempt ? [] : [{ id: 'account-referrals', label: 'Пригласить друзей', icon: 'users', onClick: () => void ctx.openDialog('auth.referrals', {}) }]),
            { id: 'account-telegram', label: me.telegram.linked ? 'Telegram: привязан' : 'Привязать Telegram (восстановление пароля)', icon: 'external', onClick: () => void ctx.openDialog('auth.telegram', {}) },
            { id: 'account-mcp', label: 'Подключить нейросеть (ИИ пишет отчёт)', icon: 'sparkles', onClick: () => void ctx.openDialog('auth.mcp', {}) },
            { id: 'account-password', label: 'Сменить пароль', icon: 'key', onClick: () => void ctx.openDialog('auth.password', {}) },
            { id: 'account-sessions', label: 'Мои сеансы и устройства', icon: 'monitor', onClick: () => void ctx.openDialog('auth.sessions', {}) },
            { id: 'account-others', label: 'Выйти на других устройствах', icon: 'logout', onClick: async () => {
              try { const r = await authApi.logoutOthers(); ctx.toast(r.revoked ? `Завершено сеансов на других устройствах: ${r.revoked}` : 'Других сеансов нет', 'ok'); }
              catch (e: any) { ctx.toast(e.message, 'error'); }
            } },
            ...(u.role === 'admin' ? [{ separator: true }, { id: 'account-admin', label: 'Администрирование', icon: 'shield-user',
              onClick: () => { location.hash = '#/admin/users'; } }] : []),
            { separator: true },
            { id: 'account-logout', label: 'Выйти', icon: 'logout', onClick: () => void leave() },
          ]} />
        </Popover>
      )}
    </>
  );
}

/** Кнопка «Администрирование» для стартового экрана (только администратору). */
export function AdminButton() {
  const me = useAuth((s) => s.me);
  if (me?.user.role !== 'admin') return null;
  return <Btn icon="shield-user" label="Администрирование" onClick={() => { location.hash = '#/admin/users'; }} testid="home-admin" />;
}

// ------------------------------------------------------------------------------------------------ смена пароля
export function ChangePasswordDialog({ close }: DialogProps<boolean>) {
  const me = useAuth((s) => s.me);
  const [old, setOld] = useState('');
  const [n1, setN1] = useState('');
  const [n2, setN2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ text: string; kind: 'error' | 'wait' }>({ text: '', kind: 'error' });
  const submit = async () => {
    if (busy) return;
    setErr({ text: '', kind: 'error' });
    try {
      if (!old) throw new Error('Введите текущий пароль');
      if (n1.length < 8) throw new Error('Пароль слишком короткий: нужно не меньше 8 символов');
      if (n1 !== n2) throw new Error('Пароли не совпадают');
      setBusy(true);
      const m = await authApi.changePassword(old, n1);
      signedIn(m);
      ctx.toast('Пароль изменён. На других устройствах нужно войти заново.', 'ok', 5000);
      close(true);
    } catch (e: any) { setErr(errorOf(e)); setBusy(false); }
  };
  return (
    <Modal title="Сменить пароль" onClose={() => close()} width={440} testid="password-dialog" footer={<>
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Сменить пароль" primary onClick={submit} disabled={busy} testid="password-submit" />
    </>}>
      <form className="au-dialog-form" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
        <PasswordField label="Текущий пароль" value={old} onChange={setOld} testid="password-old" autoComplete="current-password" autoFocus />
        <PasswordField label="Новый пароль" value={n1} onChange={setN1} testid="password-new" autoComplete="new-password" />
        <PasswordField label="Новый пароль ещё раз" value={n2} onChange={setN2} testid="password-new2" autoComplete="new-password" />
        <Rules items={passwordChecks(n1, n2, me?.user.login || '')} />
        <p className="au-muted small">После смены пароля сеансы на других устройствах завершатся.</p>
        <ErrorBox text={err.text} kind={err.kind} testid="password-error" />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ мои сеансы
/** Кратко об устройстве по User-Agent: «Edge · Windows». */
function deviceOf(ua: string): string {
  const b = /Edg\//.test(ua) ? 'Edge' : /OPR\/|Opera/.test(ua) ? 'Opera' : /YaBrowser/.test(ua) ? 'Яндекс Браузер' : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Браузер';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${b} · ${os}` : b;
}
const fmtTs = (t: number) => new Date(t * 1000).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function SessionsDialog({ close }: DialogProps<boolean>) {
  const [rows, setRows] = useState<SessionRow[] | null>(null);
  const [err, setErr] = useState('');
  const load = () => authApi.sessions().then((r) => setRows(r.sessions)).catch((e) => setErr(e.message));
  useEffect(() => { void load(); }, []);
  const end = async (id: string) => {
    try { await authApi.revokeSession(id); ctx.toast('Сеанс завершён', 'ok'); await load(); } catch (e: any) { ctx.toast(e.message, 'error'); }
  };
  return (
    <Modal title="Мои сеансы и устройства" onClose={() => close()} width={620} testid="sessions-dialog"
      footer={<Btn label="Закрыть" onClick={() => close()} />}>
      {err && <ErrorBox text={err} kind="error" />}
      {!rows ? <div className="au-muted">Загрузка…</div> : (
        <div className="au-sessions">
          {rows.map((s) => (
            <div key={s.id} className={'au-session' + (s.current ? ' is-current' : '')} data-testid="session-row">
              <Icon name="monitor" size={20} />
              <div className="au-session-text">
                <div><b>{deviceOf(s.ua)}</b>{s.current && <span className="au-session-tag">этот сеанс</span>}
                  {s.remember && <span className="au-session-tag au-dim">запомнен</span>}</div>
                <div className="au-muted small">вход {fmtTs(s.created)} · последний доступ {fmtTs(s.lastSeen)}{s.ip ? ` · ${s.ip}` : ''}</div>
              </div>
              {!s.current && <Btn icon="logout" label="Завершить" onClick={() => void end(s.id)} testid="session-end" />}
            </div>
          ))}
          <p className="au-muted small">Незнакомое устройство - завершите сеанс и смените пароль.</p>
        </div>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ чужой отчёт
/** Плашка над листом: администратор редактирует отчёт другого пользователя. */
export function ForeignBanner() {
  const f = useAuth((s) => s.foreign);
  if (!f) return null;
  const who = f.owner.name?.trim() ? `${f.owner.name} (${f.owner.login})` : f.owner.login;
  return (
    <div className="au-foreign" data-testid="foreign-banner" role="status">
      <Icon name="shield-alert" size={17} />
      <span>Отчёт пользователя <b data-testid="foreign-owner">{who}</b> - вы редактируете как администратор. Изменения сохраняются
        в его отчёт и записываются в журнал.</span>
      <Btn icon="back" label="Все отчёты" onClick={() => { void saveNow().then(() => { location.hash = '#/admin/projects'; }); }} testid="foreign-back" />
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ профиль при первом входе
const dismissKey = (uid: number) => 'welcome.dismissed.' + uid;

/** Стартовый экран: новому пользователю - предложение заполнить данные для титула. */
export function WelcomeCard() {
  const me = useAuth((s) => s.me);
  const [p, setP] = useState<Profile | null>(getProfileCached());
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    void loadProfile().then(setP).catch(() => undefined);
    return onProfile(() => setP(getProfileCached()));
  }, []);
  if (!me || !p || hidden) return null;
  if (readPref(dismissKey(me.user.id), '') === '1') return null;
  const st = p.student || ({} as any);
  const missing = [!st.student && 'Ф. И. О.', !st.group && 'группа', !st.course && 'курс', !st.direction_name && 'направление'].filter(Boolean) as string[];
  if (p.saved && !missing.length) return null;
  if (!missing.length) return null;
  return (
    <div className="au-welcome" data-testid="welcome-card">
      <div className="au-welcome-ico"><Icon name="student" size={26} /></div>
      <div className="au-welcome-text">
        <div className="au-welcome-title">Добро пожаловать{me.user.name ? ', ' + me.user.name.split(/\s+/)[0] : ''}! Заполните профиль для титула</div>
        <div className="au-muted">Не хватает: {missing.join(', ')}. Эти данные подставляются на титульный лист каждого нового отчёта;
          там же - ваши руководители и дисциплины. Проверьте и место учёбы: вуз, филиал и институт (учитесь в Архангельске -
          филиал оставьте пустым).</div>
      </div>
      <Btn icon="rename" label="Заполнить профиль" primary onClick={() => void ctx.openDialog('sto.profile', {})} testid="welcome-profile" />
      <button type="button" className="au-x" aria-label="Скрыть" title="Скрыть" data-testid="welcome-dismiss"
        onClick={() => { writePref(dismissKey(me.user.id), '1'); setHidden(true); }}><Icon name="close" size={15} /></button>
    </div>
  );
}

/** Подпись под списком отчётов: место и число отчётов пользователя. */
export function QuotaNote() {
  const q = useAuth((s) => s.me?.quota);
  if (!q) return null;
  const pct = q.quotaBytes ? Math.min(100, Math.round((q.usedBytes / q.quotaBytes) * 100)) : 0;
  return (
    <span className="au-quota-note" data-testid="quota-note" title="Место на сервере, занятое вашими отчётами">
      <Icon name="disk" size={13} /> {fmtBytes(q.usedBytes)}{q.quotaBytes ? ` из ${fmtBytes(q.quotaBytes)}` : ''}
      {q.quotaBytes ? <span className={'au-bar au-bar-inline' + (pct > 90 ? ' is-full' : '')}><span style={{ width: pct + '%' }} /></span> : null}
      {q.maxProjects ? <> · отчётов {q.projects} из {q.maxProjects}</> : null}
    </span>
  );
}
